import type {ReactElement} from 'react';
import React, {useEffect} from 'react';
import {BaseTextStrokeColorOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-text-stroke-color-option';
import type {RGBA} from '@Utils/color.util';
import {BaseTextStrokeWidthOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-text-stroke-width-option';
import {ContextualMenuToggleOption} from '@Components/contextual-menu/components/contextual-menu-toggle-option';
import {updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';
import {ContextualMenu} from '@Components/contextual-menu';
import styles from './base-text-outline-options.module.scss';

interface BaseTextOutlineOptionsProps {
  panelId: string;
  stroke: boolean;
  onClickColorFill(): void;
  strokeColor: RGBA;
  strokeWidth: number;
  isDisabled: boolean;
  updateStrokeThickness(strokeWidth: number, undoable?: boolean): void;
  onToggleStroke(): void;
  allowPanelHeightToBeSetInternally?: boolean;
  outlineColorOptionLabel?: string;
  enforceZeroLeftRightSpacing?: boolean;
}

export function BaseTextOutlineOptions({
  allowPanelHeightToBeSetInternally = true,
  enforceZeroLeftRightSpacing = false,
  ...props
}: BaseTextOutlineOptionsProps): ReactElement | null {
  useEffect((): void => {
    updateHeight(props.stroke);
  }, [props.stroke]);

  const updateStrokeThickness = (strokeWidth: number, undoable = true): void => {
    props.updateStrokeThickness(strokeWidth, undoable);
  };

  const updateHeight = (isApplied: boolean): void => {
    if (!allowPanelHeightToBeSetInternally) {
      return;
    }

    if (isApplied) {
      updateOptionsPanelHeight(props.panelId, '232px');
      return;
    }
    updateOptionsPanelHeight(props.panelId, '116px');
  };

  const onToggleOutlineOption = (): void => {
    updateHeight(!props.stroke);
    props.onToggleStroke();
  };

  const getOutlineToggle = (): ReactElement => {
    return (
      <ContextualMenuToggleOption
        id="item-outline-option"
        label={window.i18next.t('pmwjs_outline')}
        isChecked={props.stroke}
        isDisabled={props.isDisabled}
        onClick={onToggleOutlineOption}
      />
    );
  };

  const getStrokeWidthOption = (): ReactElement => {
    return <BaseTextStrokeWidthOption className="spacing-m-b-3" strokeWidth={props.strokeWidth} onUpdateStrokeWidth={updateStrokeThickness} />;
  };

  const getStrokeColorOption = (): ReactElement => {
    return (
      <BaseTextStrokeColorOption
        outlineColorOptionLabel={props.outlineColorOptionLabel}
        className="spacing-m-b-3"
        strokeColor={props.strokeColor}
        onClickColorFill={(): void => {
          props.onClickColorFill();
        }}
      />
    );
  };

  const getSubOptions = (): ReactElement | null => {
    if (!props.stroke) {
      return null;
    }

    return (
      <>
        {getStrokeColorOption()}
        {getStrokeWidthOption()}
      </>
    );
  };

  const getContent = (): ReactElement => {
    return (
      <ContextualMenu
        id="base-mobile-outline-option"
        contentClassName={`${styles.container} ${enforceZeroLeftRightSpacing ? 'spacing-m-l-0 spacing-m-r-0' : ''}`}
        hasMobileModalPadding={!enforceZeroLeftRightSpacing}
        includeLeftRightPadding={!enforceZeroLeftRightSpacing}
        hasMobileMorphingProperties
      >
        {getSubOptions()}
        {getOutlineToggle()}
      </ContextualMenu>
    );
  };
  return getContent();
}
